import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import FrameworkUtils from 'utils/framework';
import { userSelector } from 'selectors/user/userSelector';
import netBeautyBankPointsAvailableSelector from 'selectors/basket/netBeautyBankPointsAvailableSelector';
import BeautyInsiderSelector from 'selectors/beautyInsider/beautyInsiderSelector';
import ContentSelector from 'selectors/page/content/contentSelector';
import mapBenefitsViewState from 'components/Content/BeautyInsider/BeautyInsiderBenefits/mapBenefitsViewState';
import rewardsSelector from 'selectors/rewards/rewardsSelector';
import { activityPointsSelector } from 'selectors/profile/activityPointsSelector';
import { customerLimitSelector } from 'viewModel/selectors/profile/customerLimitSelector';
import userUtils from 'utils/User';
import localeUtils from 'utils/LanguageLocale';
import DateUtils from 'utils/Date';
import helperUtils from 'utils/Helpers';
import beautyInsiderActions from 'actions/BeautyInsiderActions';
import birbActions from 'actions/BIRBActions';
import rewardActions from 'actions/RewardActions';
import profileActions from 'actions/ProfileActions';
import Empty from 'constants/empty';
import contentConstants from 'constants/content';
import { filterRewardsSectionsSkuList, sortBrandSpotlightSkus } from 'utils/BeautyInsider';
import Actions from 'Actions';
import HistoryTabEN from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_en_US';
import HistoryTabFR from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_fr_CA';

const { wrapHOC } = FrameworkUtils;
const { COMPONENT_TYPES, RENDERING_TYPE } = contentConstants;

const buildFallbackItems = renderingTypes =>
    renderingTypes.map(renderingType => ({
        type: COMPONENT_TYPES.CUSTOM_RENDERING,
        renderingType
    }));

const REWARDS_TAB_FALLBACK = buildFallbackItems([
    RENDERING_TYPE.BIHUB_SAVE_ON_PURCHASE,
    RENDERING_TYPE.BIHUB_BIRTHDAY_GIFT_CAROUSEL,
    RENDERING_TYPE.BIHUB_ROUGE_ONLY_REWARDS,
    RENDERING_TYPE.BIHUB_ALL_REWARDS,
    RENDERING_TYPE.BIHUB_YOU_JUST_MISSED_THESE,
    RENDERING_TYPE.BIHUB_FOOTER_LINKS
]);

const EARN_TAB_FALLBACK = buildFallbackItems([RENDERING_TYPE.BIHUB_FOOTER_LINKS]);

const BENEFITS_TAB_FALLBACK = buildFallbackItems([
    RENDERING_TYPE.BIHUB_FEATURED_BENEFITS,
    RENDERING_TYPE.BIHUB_CREDIT_CARD_HOLDER_SUMMARY,
    RENDERING_TYPE.BIHUB_FOOTER_LINKS
]);

const TAB_SECTIONS = {
    REWARDS: { index: 0, fallback: REWARDS_TAB_FALLBACK },
    EARN: { index: 1, fallback: EARN_TAB_FALLBACK },
    BENEFITS: { index: 2, fallback: BENEFITS_TAB_FALLBACK },
    HISTORY: { index: 3, fallback: Empty.Array }
};

const { beautyInsiderSelector } = BeautyInsiderSelector;
const { contentSelector } = ContentSelector;
const { getAccountActivityPoints } = profileActions;
const getHistoryText = localeUtils.getLocalisedDataFromResource(HistoryTabEN, HistoryTabFR);
const { formatDiscountPrice, formatPrice, hideZeroCents } = helperUtils;

/**
 * Checks whether History should keep Year at a Glance in the loading state.
 *
 * @param {boolean} isBi Whether the current user is a BI member.
 * @param {Object} beautyInsider BI summary slice from Redux.
 * @returns {boolean} True when a BI user is waiting for the summary response.
 */
const isHistoryYearAtGlanceLoading = (isBi, beautyInsider) => isBi && beautyInsider?.summary === null && !beautyInsider?.summaryError;

/**
 * Checks whether History should keep Rewards Redeemed in the loading state.
 *
 * @param {boolean} isBi Whether the current user is a BI member.
 * @param {Object} rewards Rewards slice from Redux.
 * @returns {boolean} True when a BI user has no rewards response or fallback yet.
 */
const isHistoryRewardsRedeemedLoading = (isBi, rewards) =>
    isBi && Boolean(rewards?.rewardsPurchasedGroupsLoading || rewards?.rewardsPurchasedGroups === null);

/**
 * Checks whether History should keep Points History in the loading state.
 *
 * @param {boolean} isBi Whether the current user is a BI member.
 * @param {Object} activityPoints Activity points slice from Redux.
 * @returns {boolean} True when a BI user is waiting for grouped point activity.
 */
const isHistoryGroupedPointsLoading = (isBi, activityPoints) => isBi && activityPoints?.records == null && !activityPoints?.error;

/**
 * Reads credit-card rewards from the existing BI user data path.
 *
 * @param {Object} user User slice from Redux.
 * @returns {Object|undefined} Bank rewards data used by Year at a Glance.
 */
const getHistoryBankRewards = user => user?.ccRewards?.bankRewards;

const formatEmployeeDiscountAmount = amount => {
    const formattedAmount = formatDiscountPrice(amount);

    return formattedAmount ? hideZeroCents(formatPrice(`$${formattedAmount}`)) : null;
};

/**
 * Builds the History employee discount row from the same customer limit balance used by BI Summary.
 *
 * @param {Object} customerLimitBalance Employee discount limit balance from customerLimitSelector.
 * @returns {Object|null} Employee discount row source data, or null when there is no used employee discount.
 */
const getHistoryEmployeeDiscount = customerLimitBalance => {
    const balance = customerLimitBalance || Empty.Object;
    const totalSpend = Number(balance.totalSpend);
    const initial = Number(balance.initial);

    if (!Number.isFinite(totalSpend) || !Number.isFinite(initial) || initial <= 0) {
        return null;
    }

    const available = Number(balance.available);
    const formattedAvailable = Number.isFinite(available) ? formatEmployeeDiscountAmount(Math.max(0, available)) : null;
    const formattedInitial = formatEmployeeDiscountAmount(initial);
    const formattedExpirationDate = DateUtils.getDateInShortFormat(balance.initialExpiryDate);
    const subtitle =
        formattedAvailable && formattedInitial && formattedExpirationDate
            ? getHistoryText('employeeDiscountDescription', [formattedAvailable, formattedInitial, formattedExpirationDate])
            : undefined;

    return {
        text: formatEmployeeDiscountAmount(totalSpend),
        value: totalSpend,
        subtitle
    };
};

/**
 * Returns the client summary payload used by Benefits tab view-state mapping.
 *
 * @param {Object} beautyInsider BI summary slice from Redux.
 * @returns {Object} Client summary payload, or stable empty fallback before the response resolves.
 */
const getBenefitsClientSummary = beautyInsider => beautyInsider?.summary?.clientSummary || Empty.Object;

/**
 * Builds Benefits tab view state from Redux-backed user, summary, and locale data.
 *
 * @param {Object} options User, BI summary, and optional country override.
 * @returns {Object} Normalized Benefits tab view state.
 */
const getBenefitsViewState = ({ user, beautyInsider, country } = Empty.Object) =>
    mapBenefitsViewState({
        user,
        clientSummary: getBenefitsClientSummary(beautyInsider),
        country: country || (localeUtils.isCanada() ? 'CA' : 'US')
    });

const fields = createSelector(
    netBeautyBankPointsAvailableSelector,
    userSelector,
    beautyInsiderSelector,
    contentSelector,
    rewardsSelector,
    activityPointsSelector,
    customerLimitSelector,
    (netBeautyBankPointsAvailable, user, beautyInsider, content, rewards, activityPoints, customerLimitData) => {
        const isAtLeastRecognized = user.isInitialized && userUtils.isUserAtleastRecognized();
        const isBi = userUtils.isBI();
        const isSephoraEmployee = userUtils.isSephoraEmployee();
        const currentYear = beautyInsider?.summary?.clientSummary?.currentYear;
        const employeeDiscount = getHistoryEmployeeDiscount(customerLimitData?.customerLimitBalance);
        const rewardsPurchasedGroups = rewards?.rewardsPurchasedGroups || Empty.Array;
        const rewardsPurchasedGroupsError = Boolean(rewards?.rewardsPurchasedGroupsError);
        const contentSections = content.contentSections;
        const getTabContent = (section, fallback) => (section?.content?.length ? section.content : fallback);

        // Step 1: Resolve raw Rewards tab content from Contentful, falling back to hardcoded rendering types.
        const rawRewardsContent = getTabContent(contentSections?.[TAB_SECTIONS.REWARDS.index], TAB_SECTIONS.REWARDS.fallback);
        // Step 2: Strip fully out-of-stock SKUs (excluding coming-soon) from the New Rewards section.
        const newRewardsFiltered = filterRewardsSectionsSkuList(rawRewardsContent);
        // Step 3: Sort the Brand Spotlight skuList — available SKUs by points asc, OOS SKUs pushed to the back.
        const transformedRewardsTabZoneContent = sortBrandSpotlightSkus(newRewardsFiltered);

        return {
            netBeautyBankPointsAvailable,
            user,
            benefitsViewState: getBenefitsViewState({ user, beautyInsider }),
            isAtLeastRecognized,
            isBI: isBi,
            rewardsTabZone: transformedRewardsTabZoneContent,
            earnTabZone: getTabContent(contentSections?.[TAB_SECTIONS.EARN.index], TAB_SECTIONS.EARN.fallback),
            benefitsTabZone: getTabContent(contentSections?.[TAB_SECTIONS.BENEFITS.index], TAB_SECTIONS.BENEFITS.fallback),
            historyTabZone: contentSections?.[TAB_SECTIONS.HISTORY.index]?.content || Empty.Array,
            historyProfile: {
                isRecognized: isAtLeastRecognized,
                isSignedIn: userUtils.isSignedIn(),
                profileId: user.profileId,
                biAccountId: user?.beautyInsiderAccount?.biAccountId,
                isBi,
                tier: userUtils.getBiStatus()?.toLowerCase(),
                isEmployee: isSephoraEmployee,
                isCreditCardHolder: Boolean(user?.ccRewards || user?.beautyInsiderAccount?.ccRewards)
            },
            historyRegion: localeUtils.isCanada() ? 'CA' : 'US',
            historyYearAtGlance: {
                loading: isHistoryYearAtGlanceLoading(isBi, beautyInsider),
                error: Boolean(beautyInsider?.summaryError),
                data: {
                    currentYear: employeeDiscount
                        ? {
                            ...currentYear,
                            employeeDiscount
                        }
                        : currentYear,
                    bankRewards: getHistoryBankRewards(user)
                }
            },
            historyRewardsRedeemed: {
                loading: isHistoryRewardsRedeemedLoading(isBi, rewards),
                data: rewardsPurchasedGroups,
                error: rewardsPurchasedGroupsError
            },
            historyGroupedPoints: {
                loading: isHistoryGroupedPointsLoading(isBi, activityPoints),
                data: activityPoints,
                error: Boolean(activityPoints?.error)
            }
        };
    }
);

const functions = dispatch => ({
    fetchClientSummary: includeCampaigns => dispatch(beautyInsiderActions.fetchClientSummary(includeCampaigns)),
    fetchBiRewardsPromise: () => dispatch(birbActions.fetchBiRewardsPromise()),
    fetchCustomerLimit: () => dispatch(profileActions.fetchCustomerLimit()),
    fetchHistoryRewards: profileId => dispatch(rewardActions.fetchRecentlyRedeemedRewards(profileId, { purchaseFilter: 'rewards' })),
    fetchHistoryPoints: profileId => dispatch(getAccountActivityPoints(profileId)),
    showPasskeyOptinModal: args => dispatch(Actions.showPasskeyOptinModal(args)),
    showPointsHistoryInfoModal: () =>
        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getHistoryText('pointsHistoryTitle'),
                message: getHistoryText('pointsInfoModalMessage'),
                buttonText: getHistoryText('modalOk'),
                showCloseButton: true
            })
        )
});

const withBeautyInsiderTabbedProps = wrapHOC(connect(fields, functions));

export {
    withBeautyInsiderTabbedProps,
    fields,
    functions,
    getBenefitsClientSummary,
    getBenefitsViewState,
    getHistoryBankRewards,
    getHistoryEmployeeDiscount,
    isHistoryGroupedPointsLoading,
    isHistoryRewardsRedeemedLoading,
    isHistoryYearAtGlanceLoading
};
